import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { useTranslation } from 'react-i18next';

import Link45DegIcon from 'assets/images/icons/link-45deg.svg?react';
import Button from 'components/button/Button';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';
import useEditLinkPluginModal from '../../editLink/useEditLinkPluginModal';
import useEditLinkPluginSelection from '../../editLink/useEditLinkPluginSelection';

interface AddLinkButtonProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const AddLinkButton = ({
  disabled,
  toolbarSettings: { features },
}: AddLinkButtonProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const { openEditLinkModal } = useEditLinkPluginModal();
  const { isLink, linkUrl, handleLinkSubmit, selectedLinkProps } =
    useEditLinkPluginSelection();

  const handleAddLinkClick = () => {
    if (isLink) {
      openEditLinkModal({
        defaultValues: {
          openInNewWindow: selectedLinkProps?.target === '_blank',
          text: selectedLinkProps?.text ?? '',
          url: linkUrl ?? '',
        },
        onSubmit: handleLinkSubmit,
      });
    } else {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          // if a new link, first creates the link pointing to empty and then opens the modal
          // Don't use a default scheme - let the user decide (URL or variable token)
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, '');

          openEditLinkModal({
            defaultValues: {
              openInNewWindow: false,
              text: selection.focus.getNode().getTextContent(),
              url: '',
            },
            onSubmit: handleLinkSubmit,
          });
        }
      });
    }
  };

  if (!features?.link) {
    return null;
  }

  return (
    <Button
      aria-label={t('form.editor.buttons.link')}
      className={`toolbar-item ${isLink ? 'active' : ''}`}
      data-testid="toolbar-button-link"
      disabled={disabled}
      onClick={handleAddLinkClick}
    >
      <Link45DegIcon />
    </Button>
  );
};

export default AddLinkButton;
